import { FC, useRef, useState } from 'react';
import SimpleMenu from 'components/SimpleMenu';
import useAppNavigation from 'hooks/useAppNavigation';
import { KEYBOARD_KEYS } from '@constants';
import { TextField } from '@mui/material';

export interface ImpersonateAccountMenuProps {
  target: HTMLElement;
  accountId: number;
  teamId: number;
  tags: string[];
  tenant?: string;
  realmId?: string;
  onClose: VoidFunction;
}

const defaultWebAppLocalHostPort = 8080;

export const ImpersonateAccountMenu: FC<ImpersonateAccountMenuProps> = ({
  target,
  accountId,
  teamId,
  tags,
  tenant,
  realmId,
  onClose,
}) => {
  const { navigateToMosaicAppImpersonated } = useAppNavigation();
  const [isEditingCustomUrl, setIsEditingCustomUrl] = useState<boolean>(false);
  const customUrlInputRef = useRef<HTMLInputElement>(null);

  const onDefaultImpersonation = () => {
    void navigateToMosaicAppImpersonated({ accountId, teamId, tags, tenantName: tenant, realmId });
  };

  const onImpersonateWithCustomUrl = () => {
    void navigateToMosaicAppImpersonated({
      accountId,
      teamId,
      urlDomain: customUrlInputRef.current?.value,
      tags,
      tenantName: tenant,
      realmId,
    });
  };

  const onImpersonateIntoLocalHost = () => {
    void navigateToMosaicAppImpersonated({
      accountId,
      teamId,
      urlDomain: `localhost:${defaultWebAppLocalHostPort}`,
      tags,
      tenantName: tenant,
      realmId,
    });
  };

  const onEditCustomUrl = () => {
    setIsEditingCustomUrl(true);
  };

  const items = [
    {
      label: 'Impersonate',
      shouldCloseMenuOnClick: false,
      onClick: onDefaultImpersonation,
      tooltip: 'Default impersonation behaviour',
    },
    {
      shouldCloseMenuOnClick: false,
      ...(isEditingCustomUrl
        ? {
            label: (
              <TextField
                fullWidth
                placeholder="any-custom-url-you-want.com"
                inputRef={customUrlInputRef}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  const { key } = e;
                  if (key === KEYBOARD_KEYS.ENTER) {
                    onImpersonateWithCustomUrl();
                  }
                }}
              />
            ),
            tooltip: 'Type domain you wanted to impersonate into and press Enter',
          }
        : {
            label: 'Impersonate with custom url domain',
            tooltip:
              'Impersonate this member into custom inputted domain (ie: preview channel). Useful for devs testing',
            onClick: onEditCustomUrl,
          }),
    },
    {
      shouldCloseMenuOnClick: false,
      label: `Impersonate into localhost`,
      tooltip: `Impersonate into Web Application's localhost:${defaultWebAppLocalHostPort}. Useful for devs`,
      onClick: onImpersonateIntoLocalHost,
    },
  ];

  return <SimpleMenu anchorEl={target} items={items} handleClose={onClose} />;
};
